/**
 * AUTH59 — Overview spatial companion／filter／狀態純函式。
 * Stay Pins Batch B：bounded Stay markers／companion／day interval filter。
 */
import {
  MARKER_KIND_ACTIVITY,
  MARKER_KIND_STAY,
  SPATIAL_GROUP_STAYS,
  SPATIAL_GROUP_UNSCHEDULED,
  SPATIAL_UNSCHEDULED_DAY_LABEL,
  buildJourneySpatialMarkers,
  buildStaySpatialMarkers,
  resolveSpatialLocationLabels,
  stayMarkerKey,
  toPlanningCompatibleMapMarker,
  isSpatialMarkerEligible,
} from './journeySpatialMarkerModel';
import {
  activityStatusProductLabel,
  formatPlanningDayHeading,
} from '../journey-planning/planningPresentation';
import { normalizePlanningSchedule } from '../journey-planning/planningModel';
import { calendarDatePart } from '../journey-today/journeyTodayModel';

export const SPATIAL_PHASE = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
  OFFLINE: 'offline',
});

/** Overview 預設空間篩選：全部。 */
export const SPATIAL_FILTER_ALL = 'ALL';

function compareActivityOrder(a, b) {
  const orderA = Number(a?.displayOrder) || 0;
  const orderB = Number(b?.displayOrder) || 0;
  if (orderA !== orderB) return orderA - orderB;
  const idA = String(a?.activityId || '');
  const idB = String(b?.activityId || '');
  if (idA < idB) return -1;
  if (idA > idB) return 1;
  return 0;
}

function normalizeSchedule(schedule) {
  return schedule?.days ? schedule : normalizePlanningSchedule(schedule || {});
}

function normalizeAccommodations(accommodations) {
  return Array.isArray(accommodations) ? accommodations : [];
}

function companionItem(activity) {
  const labels = resolveSpatialLocationLabels(activity);
  const status = activity?.status || 'PLANNED';
  const activityId = String(activity.activityId);
  return {
    kind: MARKER_KIND_ACTIVITY,
    selectionKey: activityId,
    activityId,
    accommodationId: null,
    title: activity.title || 'Untitled activity',
    locationLabel: labels.locationLabel,
    locationAddress: labels.locationAddress,
    hasCoordinates: Boolean(activity.hasCoordinates),
    status,
    statusLabel: activityStatusProductLabel(status),
    isCancelled: status === 'CANCELLED',
    checkInAt: null,
    checkOutAt: null,
  };
}

function stayCompanionItem(stay) {
  const accommodationId = String(stay.accommodationId || '');
  const location = stay.location;
  const hasCoordinates = isSpatialMarkerEligible(location || {});
  const locationLabel = location?.name
    || stay.locationLabel
    || (stay.locationId ? '已選地點' : '未設定地點');
  const locationAddress = location?.address || stay.addressText || '';
  return {
    kind: MARKER_KIND_STAY,
    selectionKey: stayMarkerKey(accommodationId),
    activityId: null,
    accommodationId,
    title: stay.propertyName || 'Untitled stay',
    locationLabel,
    locationAddress,
    hasCoordinates,
    status: 'STAY',
    statusLabel: '住宿',
    isCancelled: false,
    checkInAt: stay.checkInAt || null,
    checkOutAt: stay.checkOutAt || null,
  };
}

function dayGroupKey(day) {
  return String(day.journeyDayId || `day-order:${day.displayOrder}`);
}

/**
 * Companion 分組：活動日／未排程＋住宿分組（含無座標 Stay）。
 * 不 mutate schedule／accommodations。
 */
export function buildSpatialCompanionGroups(schedule, accommodations = []) {
  const normalized = normalizeSchedule(schedule);
  const stays = normalizeAccommodations(accommodations);
  const groups = [];

  const days = [...(normalized.days || [])].sort((a, b) => (
    (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0)
  ));
  days.forEach((day) => {
    const activities = [...(day.activities || [])].sort(compareActivityOrder);
    if (!activities.length) return;
    groups.push({
      key: dayGroupKey(day),
      dayLabel: formatPlanningDayHeading(day),
      unscheduled: false,
      calendarDate: day.date || null,
      items: activities.map(companionItem),
    });
  });

  const unscheduled = [...(normalized.unscheduledActivities || [])].sort(compareActivityOrder);
  if (unscheduled.length) {
    groups.push({
      key: SPATIAL_GROUP_UNSCHEDULED,
      dayLabel: SPATIAL_UNSCHEDULED_DAY_LABEL,
      unscheduled: true,
      calendarDate: null,
      items: unscheduled.map(companionItem),
    });
  }

  if (stays.length) {
    groups.push({
      key: SPATIAL_GROUP_STAYS,
      dayLabel: '住宿',
      unscheduled: false,
      calendarDate: null,
      items: stays.map(stayCompanionItem),
    });
  }

  return groups;
}

export function countCompanionActivities(groups) {
  return (groups || []).reduce((total, group) => total + (group.items?.length || 0), 0);
}

/**
 * Filter chips：全部 → 有活動的行程日 → 未排程 → 住宿（若存在）。
 */
export function buildSpatialFilterOptions(schedule, accommodations = []) {
  const normalized = normalizeSchedule(schedule);
  const stays = normalizeAccommodations(accommodations);
  const options = [{
    key: SPATIAL_FILTER_ALL,
    label: '全部',
    kind: 'all',
  }];

  const days = [...(normalized.days || [])].sort((a, b) => (
    (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0)
  ));
  days.forEach((day) => {
    const order = Number(day.displayOrder) || 0;
    options.push({
      key: dayGroupKey(day),
      label: order > 0 ? `第 ${order} 天` : formatPlanningDayHeading(day),
      kind: 'day',
      dayNumber: order,
      journeyDayId: day.journeyDayId == null ? null : String(day.journeyDayId),
      dayLabel: formatPlanningDayHeading(day),
      calendarDate: day.date || null,
    });
  });

  const unscheduled = normalized.unscheduledActivities || [];
  if (unscheduled.length) {
    options.push({
      key: SPATIAL_GROUP_UNSCHEDULED,
      label: SPATIAL_UNSCHEDULED_DAY_LABEL,
      kind: 'unscheduled',
    });
  }

  if (stays.length) {
    options.push({
      key: SPATIAL_GROUP_STAYS,
      label: '住宿',
      kind: 'stays',
    });
  }

  return options;
}

export function filterSpatialCompanionGroups(
  groups,
  filterKey,
  accommodations = [],
  filterMeta = null,
) {
  const key = filterKey || SPATIAL_FILTER_ALL;
  const list = Array.isArray(groups) ? groups : [];
  if (key === SPATIAL_FILTER_ALL) return list;
  if (key === SPATIAL_GROUP_STAYS) {
    return list.filter((group) => group.key === SPATIAL_GROUP_STAYS);
  }
  if (key === SPATIAL_GROUP_UNSCHEDULED) {
    return list.filter((group) => group.key === SPATIAL_GROUP_UNSCHEDULED);
  }

  // Day filter：該日活動組＋依 [checkIn,checkOut) 可見的住宿
  const dayGroup = list.find((group) => group.key === key && !group.unscheduled);
  const result = [];
  if (dayGroup) result.push(dayGroup);

  const calendarDate = filterMeta?.calendarDate
    || dayGroup?.calendarDate
    || list.find((group) => group.key === key)?.calendarDate
    || null;
  const stayGroup = list.find((group) => group.key === SPATIAL_GROUP_STAYS);
  if (stayGroup && calendarDate) {
    const visibleIds = new Set(
      accommodations.filter((stay) => isStayVisibleOnCalendarDate(stay, calendarDate))
        .map((stay) => String(stay.accommodationId || '')),
    );
    const items = (stayGroup.items || []).filter((item) => visibleIds.has(String(item.accommodationId || '')));
    if (items.length) {
      result.push({ ...stayGroup, items });
    }
  }
  return result;
}

export function filterSpatialMarkers(spatialMarkers, filterKey, accommodations = [], filterMeta = null) {
  const list = Array.isArray(spatialMarkers) ? spatialMarkers : [];
  const key = filterKey || SPATIAL_FILTER_ALL;
  if (key === SPATIAL_FILTER_ALL) return list;
  if (key === SPATIAL_GROUP_UNSCHEDULED) {
    return list.filter((marker) => marker.group === SPATIAL_GROUP_UNSCHEDULED);
  }
  if (key === SPATIAL_GROUP_STAYS) {
    return list.filter((marker) => marker.kind === MARKER_KIND_STAY || marker.group === SPATIAL_GROUP_STAYS);
  }

  const activityMarkers = list.filter((marker) => (
    (marker.kind || MARKER_KIND_ACTIVITY) !== MARKER_KIND_STAY
    && (marker.group === key || String(marker.journeyDayId || '') === key)
  ));

  const calendarDate = filterMeta?.calendarDate || null;
  let stayMarkers = [];
  if (calendarDate) {
    const visibleIds = new Set(
      accommodations.filter((stay) => isStayVisibleOnCalendarDate(stay, calendarDate))
        .map((stay) => String(stay.accommodationId || '')),
    );
    stayMarkers = list.filter((marker) => (
      marker.kind === MARKER_KIND_STAY
      && visibleIds.has(String(marker.accommodationId || ''))
    ));
  }
  return [...activityMarkers, ...stayMarkers];
}

function validCalendarDatePart(value) {
  const part = calendarDatePart(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return '';
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthDays = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= monthDays[month - 1] ? part : '';
}

/** Selected-day Stay visibility; pure calendar comparison, without timezone conversion. */
export function isStayVisibleOnCalendarDate(stay, calendarDate) {
  const selected = validCalendarDatePart(calendarDate);
  const checkIn = validCalendarDatePart(stay?.checkInAt);
  const checkOut = validCalendarDatePart(stay?.checkOutAt);
  if (!selected || !checkIn || !checkOut || checkIn >= checkOut) return false;
  return checkIn <= selected && selected < checkOut;
}

/**
 * Overview map markers（Activity + Stay）；Planning-compatible shape。
 */
export function buildOverviewSpatialMapMarkers(
  schedule,
  filterKey = SPATIAL_FILTER_ALL,
  accommodations = [],
  filterMeta = null,
) {
  const normalized = normalizeSchedule(schedule);
  const activitySpatial = buildJourneySpatialMarkers(normalized, {
    formatDayLabel: formatPlanningDayHeading,
  });
  const staySpatial = buildStaySpatialMarkers(accommodations);
  const merged = [...activitySpatial, ...staySpatial];
  return filterSpatialMarkers(merged, filterKey, accommodations, filterMeta)
    .map(toPlanningCompatibleMapMarker);
}

/** 篩選變更後：若選取 marker 不在可見 companion，回傳 ''。 */
export function resolveSpatialSelectionAfterFilter(selectedMarkerKey, companionGroups) {
  const selected = selectedMarkerKey == null ? '' : String(selectedMarkerKey);
  if (!selected) return '';
  const visible = (companionGroups || []).some(
    (group) => (group.items || []).some((item) => (
      String(item.selectionKey || item.activityId || '') === selected
    )),
  );
  return visible ? selected : '';
}

/**
 * schedule 請求失敗分類：authoritative denial vs 一般 unavailable。
 */
export function classifySpatialScheduleFailure(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 92001 || code === 93001 || key.includes('not.found')) return 'denied';
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'denied';
  return 'unavailable';
}
